import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { 
  Building2, ChevronLeft, ChevronRight, GraduationCap, 
  Menu, X, Search, BookOpen, Users, User, LogOut, 
  Settings, Upload, BarChart, Shield, LayoutDashboard,
  Calendar, FileText, Home, Book, ExternalLink,
  ChevronDown, FolderOpen, Eye, FileUp,
  ClipboardCheck, School, Database, FileCheck, MessageSquare
} from 'lucide-react';
import { publicAPI } from '../../services/api';
import dduLogo from './ddulogo.png'; 
import LoginModal from '../auth/LoginModal';
import { adminAPI, schoolAdminAPI } from '../../services/api';

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [schools, setSchools] = useState([]);
  const [loadingSchools, setLoadingSchools] = useState(true);
  
  // Navigation states
  const [activeSchoolId, setActiveSchoolId] = useState(null);
  const [schoolDepts, setSchoolDepts] = useState({}); 
  const [loadingDepts, setLoadingDepts] = useState(false);
  
  // Dropdown states
  const [selectedDeptId, setSelectedDeptId] = useState(null);
  const [deptYears, setDeptYears] = useState({});
  const [loadingYears, setLoadingYears] = useState({});
  
  // UI states
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [expandedMobileSection, setExpandedMobileSection] = useState('nav'); 
  const [expandedMobileSchool, setExpandedMobileSchool] = useState(null);
  const [expandedMobileDept, setExpandedMobileDept] = useState(null);
  const dropdownRef = useRef(null);
  const schoolsContainerRef = useRef(null);
  const dropdownTimeoutRef = useRef(null);
  const [showLeftScroll, setShowLeftScroll] = useState(false);
  const [showRightScroll, setShowRightScroll] = useState(true);
  const [dropdownPosition, setDropdownPosition] = useState({ left: 0, top: 0, width: 0, schoolId: null });
  
  // Auth states
  const [modalConfig, setModalConfig] = useState({
    isOpen: false,
    userType: 'teacher',
    defaultMode: 'login'
  });
  const [user, setUser] = useState(() => {
    const savedUser = localStorage.getItem('user');
    return savedUser ? JSON.parse(savedUser) : null;
  });
  const [loginSuccess, setLoginSuccess] = useState(false);
  const [unreadFeedbackCount, setUnreadFeedbackCount] = useState(0);

  // Fetch schools
  useEffect(() => {
    const fetchSchools = async () => {
      try {
        const res = await publicAPI.getSchools();
        setSchools(res.data?.schools || res.data || []);
      } catch (err) { 
        console.error(err);
        setSchools([]);
      } finally { 
        setLoadingSchools(false); 
      }
    };
    fetchSchools();
  }, []);

  // Fetch Feedback Count
  useEffect(() => {
    const fetchUnreadCount = async () => {
      if (!user) return;
      try {
        let res;
        if (user.role === 'admin') {
          res = await adminAPI.getFeedback({ status: 'new', excludeSchools: true });
        } else if (user.role === 'school_admin') {
          res = await schoolAdminAPI.getFeedback({ status: 'new' });
        } else {
          return;
        }
        
        const feedbackData = res.data?.data || res.data || [];
        setUnreadFeedbackCount(feedbackData.length);
      } catch (err) {
        console.error('Failed to fetch unread feedback:', err);
      }
    };

    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000); // 30s
    return () => clearInterval(interval);
  }, [user, location.pathname]);

  // Set active school from URL
  useEffect(() => {
    const pathParts = location.pathname.split('/');
    if (pathParts[1] === 'school' && pathParts[2]) {
      setActiveSchoolId(pathParts[2]);
    } else {
      setActiveSchoolId(null);
    }

    // Check for register parameter
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('register') === 'true' && !user) {
      setModalConfig({
        isOpen: true,
        userType: 'teacher',
        defaultMode: 'register'
      });
      // Clear the param after opening to prevent repeated openings
      navigate(location.pathname, { replace: true });
    }
  }, [location, user, navigate]);

  // Check scroll position for schools container
  const checkScrollPosition = () => {
    if (schoolsContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = schoolsContainerRef.current;
      setShowLeftScroll(scrollLeft > 10);
      setShowRightScroll(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  // Add mouse wheel scrolling for schools
  useEffect(() => {
    const container = schoolsContainerRef.current;
    
    const handleWheel = (e) => {
      if (container && Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
        e.preventDefault();
        container.scrollLeft += e.deltaY;
      }
    };

    if (container) {
      container.addEventListener('wheel', handleWheel, { passive: false });
      
      return () => {
        container.removeEventListener('wheel', handleWheel);
      };
    }
  }, []);

  // Initialize scroll check
  useEffect(() => {
    const container = schoolsContainerRef.current;
    if (container) {
      setTimeout(checkScrollPosition, 100);
      container.addEventListener('scroll', checkScrollPosition);
      
      return () => {
        container.removeEventListener('scroll', checkScrollPosition);
      };
    }
  }, [schools, loadingSchools]);

  // Scroll schools container
  const scrollSchools = (direction) => {
    if (schoolsContainerRef.current) {
      const scrollAmount = 200;
      schoolsContainerRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  // Handle login success
  const handleLoginSuccess = (loginResponseData) => {
    const { user, token } = loginResponseData;
    setUser(user);
    localStorage.setItem('user', JSON.stringify(user));
    localStorage.setItem('token', token);
    setLoginSuccess(true);
    closeModal();
    
    setTimeout(() => {
      if (user.role === 'admin') {
        navigate('/admin');
      } else if (user.role === 'teacher') {
        navigate('/teachers');
      } else if (user.role === 'school_admin') {
        navigate('/school-admin');
      } else {
        navigate('/');
      }
    }, 100);
  };

  // Show success notification
  useEffect(() => {
    if (loginSuccess && user) {
      const timer = setTimeout(() => {
        setLoginSuccess(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [loginSuccess, user]);


  // In your Header component, add this useEffect to close dropdown on route change
// Add this near your other useEffect hooks (around line 100-120)

// Close dropdown when route changes
useEffect(() => {
  closeDropdown();
  setIsMenuOpen(false); // Also close mobile menu
}, [location.pathname]); // This runs whenever the URL path changes



  // Handle school hover - INSTANT RESPONSE
  const handleSchoolHover = (schoolId, event) => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
    }
    
    // Calculate dropdown position immediately
    if (event && event.currentTarget) {
      const rect = event.currentTarget.getBoundingClientRect();
      const containerScrollLeft = schoolsContainerRef.current?.scrollLeft || 0;
      
      // Calculate position relative to viewport (not affected by page scroll)
      setDropdownPosition({
        left: rect.left,
        top: rect.bottom,
        width: rect.width,
        schoolId: schoolId
      });
      
      // Set active school immediately WITHOUT delay
      setActiveSchoolId(schoolId);
      setSelectedDeptId(null);
      
      // Load departments if not loaded
      if (!schoolDepts[schoolId]) {
        setLoadingDepts(true);
        publicAPI.getSchoolDepartments(schoolId)
          .then(response => {
            setSchoolDepts(prev => ({ ...prev, [schoolId]: response.data || [] }));
          })
          .catch(err => console.error(err))
          .finally(() => setLoadingDepts(false));
      }
    }
  };

  // Handle school mouse leave
  const handleSchoolMouseLeave = () => {
    dropdownTimeoutRef.current = setTimeout(() => {
      // Don't close if mouse moved to dropdown
      if (!dropdownRef.current?.contains(document.activeElement)) {
        closeDropdown();
      }
    }, 100);
  };

  // Handle school click
  const handleSchoolClick = (schoolId, event) => {
    if (activeSchoolId === schoolId) {
      closeDropdown();
    } else {
      // Calculate dropdown position on click
      if (event && event.currentTarget) {
        const rect = event.currentTarget.getBoundingClientRect();
        setDropdownPosition({
          left: rect.left,
          top: rect.bottom,
          width: rect.width,
          schoolId: schoolId
        });
      }
      
      setActiveSchoolId(schoolId);
      setSelectedDeptId(null);
      
      if (!schoolDepts[schoolId]) {
        setLoadingDepts(true);
        publicAPI.getSchoolDepartments(schoolId)
          .then(response => {
            setSchoolDepts(prev => ({ ...prev, [schoolId]: response.data || [] }));
          })
          .catch(err => console.error(err))
          .finally(() => setLoadingDepts(false));
      }
    }
  };

  // Load department years
  const loadDepartmentYears = async (deptId, schoolId) => {
    const cacheKey = `${schoolId}-${deptId}`;
    if (!deptYears[cacheKey]) {
      setLoadingYears(prev => ({ ...prev, [cacheKey]: true }));
      try {
        const response = await publicAPI.getDepartmentById(deptId);
        const departmentData = response.data || response;
        const activeYears = departmentData.activeYears || [];
        const sortedYears = [...activeYears].sort((a, b) => b - a);
        
        setDeptYears(prev => ({ 
          ...prev, 
          [cacheKey]: sortedYears 
        }));
      } catch (err) { 
        console.error("Failed to load department years:", err);
        setDeptYears(prev => ({ 
          ...prev, 
          [cacheKey]: [] 
        }));
      }
      finally {
        setLoadingYears(prev => ({ ...prev, [cacheKey]: false }));
      }
    }
  };

  // Department click handler
  const handleDeptClick = (deptId) => {
    if (selectedDeptId === deptId) {
      setSelectedDeptId(null);
    } else {
      setSelectedDeptId(deptId);
      loadDepartmentYears(deptId, activeSchoolId);
    }
  };

  // Get year label
  const getYearLabel = (yearNumber) => {
    const yearMap = {
      1: 'First Year',
      2: 'Second Year', 
      3: 'Third Year',
      4: 'Fourth Year',
      5: 'Fifth Year',
      6: 'Sixth Year',
      7: 'Seventh Year',
      8: 'Eighth Year'
    };
    return yearMap[yearNumber] || `Year ${yearNumber}`;
  };

  // Year selection handler
  const handleYearSelect = (schoolId, deptId, year) => {
    navigate(`/school/${schoolId}/department/${deptId}/year/${year}`);
    closeDropdown();
  };

  // Close dropdown
  const closeDropdown = () => {
    setActiveSchoolId(null);
    setSelectedDeptId(null);
    setIsMenuOpen(false);
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
    }
  };

  // Handle dropdown mouse leave
  const handleDropdownMouseLeave = () => {
    dropdownTimeoutRef.current = setTimeout(() => {
      closeDropdown();
    }, 300);
  };

  // Handle dropdown mouse enter
  const handleDropdownMouseEnter = () => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
    }
  };

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        closeDropdown();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (dropdownTimeoutRef.current) {
        clearTimeout(dropdownTimeoutRef.current);
      }
    };
  }, []);

  // Update dropdown position when scrolling (but keep it under the school link)
  useEffect(() => {
    const updateDropdownPosition = () => {
      if (activeSchoolId && schoolsContainerRef.current) {
        // Find the active school link
        const schoolLinks = schoolsContainerRef.current.querySelectorAll('a');
        schoolLinks.forEach(link => {
          if (link.getAttribute('href')?.includes(activeSchoolId)) {
            const rect = link.getBoundingClientRect();
            // Update position based on current viewport position
            setDropdownPosition(prev => ({
              ...prev,
              left: rect.left,
              top: rect.bottom
            }));
          }
        });
      }
    };

    // Update position on scroll and resize
    window.addEventListener('scroll', updateDropdownPosition);
    window.addEventListener('resize', updateDropdownPosition);
    schoolsContainerRef.current?.addEventListener('scroll', updateDropdownPosition);
    
    return () => {
      window.removeEventListener('scroll', updateDropdownPosition);
      window.removeEventListener('resize', updateDropdownPosition);
      schoolsContainerRef.current?.removeEventListener('scroll', updateDropdownPosition);
    };
  }, [activeSchoolId]);

  // Auth handlers
  const openLoginModal = () => {
    setModalConfig({ isOpen: true, userType: 'teacher', defaultMode: 'login' });
    setIsMenuOpen(false);
  };

  const closeModal = () => {
    setModalConfig({ ...modalConfig, isOpen: false });
  };

  const handleLogout = () => {
    setUser(null);
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    navigate('/');
  };

  // Get dashboard items based on role - FIXED TO MATCH YOUR ROUTES
  const getDashboardItems = () => {
    if (user?.role === 'admin') {
      return [
        { path: '/admin?tab=approvals', label: 'Pending Approvals', icon: ClipboardCheck },
        { path: '/admin?tab=feedback', label: 'User Feedback', icon: MessageSquare, count: unreadFeedbackCount },
        { path: '/admin?tab=schools', label: 'School Admin', icon: Building2 },
        { path: '/admin?tab=dashboard', label: 'Detailed Stats', icon: BarChart },
        { path: '/admin?tab=teachers', label: 'Registry', icon: Users },
        { path: '/admin?tab=settings', label: 'Settings', icon: Settings },
      ];
    } else if (user?.role === 'school_admin') {
      return [
        { path: '/school-admin?tab=approvals', label: 'Staff Approvals', icon: ClipboardCheck },
        { path: '/school-admin?tab=feedback', label: 'School Feedback', icon: MessageSquare, count: unreadFeedbackCount },
        { path: '/school-admin?tab=departments', label: 'Departments', icon: GraduationCap },
        { path: '/school-admin?tab=courses', label: 'Course Catalog', icon: BookOpen },
        { path: '/school-admin?tab=profile', label: 'Admin Settings', icon: Settings },
      ];
    } else if (user?.role === 'teacher') {
      return [
        { path: '/teachers?tab=upload', label: 'Upload Materials', icon: Upload },
        { path: '/teachers?tab=materials', label: 'My Projects', icon: FolderOpen },
        { path: '/teachers?tab=dashboard', label: 'My Analytics', icon: BarChart },
        { path: '/teachers?tab=profile', label: 'Account Settings', icon: Settings },
      ];
    }
    return [];
  };

  // Get main navigation items based on user role
  const getMainNavItems = () => {
    const baseItems = [
      { path: '/', label: 'Home', icon: Home },
      { path: '/search', label: 'Search Materials', icon: Search },
    ];

    // Add dashboard link for all users (logged in or not)
    if (user) {
      if (user.role === 'admin') {
        baseItems.splice(1, 0, { path: '/admin', label: 'Admin Panel', icon: Shield });
      } else if (user.role === 'school_admin') {
        baseItems.splice(1, 0, { path: '/school-admin', label: 'School Portal', icon: Shield });
      } else if (user.role === 'teacher') {
        baseItems.splice(1, 0, { path: '/teachers', label: 'Library', icon: BookOpen });
      } else {
        // For students or other roles
        baseItems.splice(1, 0, { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard });
      }
    } else {
      // For non-logged in users (public/students)
      baseItems.splice(1, 0, { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard });
    }

    return baseItems;
  };

  const dashboardItems = getDashboardItems();
  const dropdownDashboardItems = dashboardItems;
  const mainNavItems = getMainNavItems();
  const isAdmin = user?.role === 'admin';
  const isSchoolAdmin = user?.role === 'school_admin';
  const isAdminTier = isAdmin || isSchoolAdmin;

  const currentSchool = schools.find(s => s._id === activeSchoolId);
  const departments = activeSchoolId ? (schoolDepts[activeSchoolId] || []) : [];

  return (
    <>
      <header className="sticky top-0 z-[9999] bg-white shadow-lg border-b">
        {/* Login Success Notification */}
        {loginSuccess && (
          <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white py-2 px-4 text-center">
            <div className="container mx-auto flex items-center justify-center gap-2">
              <span className="font-semibold">✓ Login successful!</span>
              <span>
                {user.role === 'admin'
                  ? 'Redirecting to Admin Portal...' 
                  : user.role === 'school_admin'
                  ? 'Redirecting to School Admin Portal...'
                  : 'Redirecting to Teacher Portal...'
                }
              </span>
            </div>
          </div>
        )}

        {/* Top Bar - Logo and Login */}
        <div className="bg-gradient-to-r from-blue-900 to-blue-800 text-white">
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-between py-3">
              {/* Logo */}
              <Link to="/" className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full overflow-hidden bg-white border-2 border-blue-300">
                  <img 
                    src={dduLogo}
                    alt="Dire Dawa University"
                    className="w-full h-full object-cover"
                  />
                </div>
                
                <div className="hidden sm:block">
                  <h1 className="text-lg font-bold text-white leading-tight">
                    Dire Dawa University
                  </h1>
                  <p className="text-xs text-blue-200">
                    Learning Repository System
                  </p>
                </div>
              </Link>

              {/* Login/User Area */}
              <div className="flex items-center gap-4">
                {!user ? (
                  <button
                    onClick={openLoginModal}
                    className="px-4 py-2 bg-white text-blue-900 text-sm font-semibold rounded-lg hover:bg-blue-100 transition-colors flex items-center gap-2"
                  >
                    <User className="w-4 h-4" />
                    Login / Register
                  </button>
                ) : (
                  <div className="relative group">
                    <button className="flex items-center gap-2 px-4 py-2 bg-white text-blue-900 text-sm font-semibold rounded-lg hover:bg-blue-100 transition-colors">
                      {isAdminTier ? (
                        <Shield className={`w-4 h-4 ${isAdmin ? 'text-purple-600' : 'text-blue-600'}`} />
                      ) : (
                        <User className="w-4 h-4 text-blue-600" />
                      )}
                      <span className="font-medium">
                        {isAdmin ? 'Admin' : isSchoolAdmin ? 'School Admin' : user.fullName?.split(' ')[0] || 'Teacher'}
                      </span>
                      <ChevronDown className="w-3 h-3" />
                    </button>
                    
                    {/* Dashboard Dropdown - FIXED WITH CORRECT ROUTES */}
                    <div className="absolute right-0 top-full mt-1 w-64 bg-white border border-gray-300 rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                      <div className="p-2">
                        {/* User Info */}
                        <div className="px-3 py-3 border-b border-gray-200 mb-1">
                          <div className="flex items-center gap-3">
                            <div className={`p-2 rounded-full ${isAdminTier ? (isAdmin ? 'bg-purple-100' : 'bg-blue-100') : 'bg-gray-100'}`}>
                              {isAdminTier ? (
                                <Shield className={`w-5 h-5 ${isAdmin ? 'text-purple-600' : 'text-blue-600'}`} />
                              ) : (
                                <User className="w-5 h-5 text-blue-600" />
                              )}
                            </div>
                            <div>
                              <div className="font-semibold text-gray-800 text-sm">
                                {isAdmin ? 'Admin' : isSchoolAdmin ? 'School Admin' : 'Academic Staff'}
                              </div>
                              <p className="text-xs text-gray-500 truncate max-w-[180px]">
                                {user.fullName || user.email}
                              </p>
                            </div>
                          </div>
                        </div>
                        
                        {/* Role-specific quick actions */}
                        <div className="mb-2">
                          <div className="px-3 py-1 text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Quick Actions
                          </div>
                          {dropdownDashboardItems.map((item) => (
                            <Link
                              key={item.path}
                              to={item.path}
                              className="flex items-center justify-between px-3 py-2.5 text-sm text-gray-700 hover:bg-blue-50 rounded transition-all group"
                              onClick={() => {
                                if (window.innerWidth < 768) {
                                  document.activeElement?.blur();
                                }
                              }}
                            >
                              <div className="flex items-center gap-3">
                                <item.icon className="w-4 h-4 text-gray-400 group-hover:text-blue-600 transition-colors" />
                                <span className="group-hover:text-blue-900 font-medium">{item.label}</span>
                              </div>
                              {item.count > 0 && (
                                <span className="bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full flex items-center justify-center min-w-[18px]">
                                  {item.count > 99 ? '99+' : item.count}
                                </span>
                              )}
                            </Link>
                          ))}
                        </div>
                        
                        {/* View All Link */}
                        <div className="border-t border-gray-200 pt-2">
                          <Link
                            to={isAdmin ? '/admin' : isSchoolAdmin ? '/school-admin' : '/teachers'}
                            className="flex items-center gap-3 px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded transition-colors font-medium"
                            onClick={() => {
                              if (window.innerWidth < 768) {
                                document.activeElement?.blur();
                              }
                            }}
                          >
                            <LayoutDashboard className="w-4 h-4" />
                            {isAdmin ? 'View Admin Portal' : isSchoolAdmin ? 'View School Portal' : 'View Teacher Portal'}
                          </Link>
                        </div>
                        
                        {/* Logout */}
                        <button
                          onClick={handleLogout}
                          className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-red-600 hover:bg-red-50 rounded transition-colors mt-2 border-t border-gray-200 pt-2"
                        >
                          <LogOut className="w-4 h-4" />
                          Logout
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                
                <button 
                  className="md:hidden p-2 text-white/80 hover:text-white transition-all"
                  onClick={() => setIsMenuOpen(!isMenuOpen)}
                >
                  {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Main Navigation */}
        <div className="bg-white border-b border-gray-200">
          <div className="container mx-auto px-4">
            <nav className="hidden md:flex items-center gap-6 py-2">
              {mainNavItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className="flex items-center gap-2 text-gray-700 hover:text-blue-600 font-medium transition-colors relative group"
                >
                  <item.icon className="w-4 h-4" />
                  {item.label}
                  <div className="absolute -bottom-1 left-0 w-0 h-0.5 bg-blue-600 group-hover:w-full transition-all duration-300"></div>
                </Link>
              ))}
            </nav>
          </div>
        </div>

        {/* Schools Navigation Bar */}
        <div className="bg-gradient-to-r from-blue-50 to-gray-50 border-t border-gray-300 relative">
          <div className="relative">
            {/* Scroll Buttons */}
            <button
              onClick={() => scrollSchools('left')}
              className={`absolute left-0 top-1/2 transform -translate-y-1/2 z-30 w-8 h-12 flex items-center justify-center bg-white/90 backdrop-blur-sm border border-gray-400 rounded-r-lg hover:bg-white hover:border-blue-500 hover:shadow-lg transition-all ${!showLeftScroll ? 'opacity-50 cursor-not-allowed' : ''}`}
              disabled={!showLeftScroll}
            >
              <ChevronLeft className="w-5 h-5 text-gray-600" />
            </button>

            <button
              onClick={() => scrollSchools('right')}
              className={`absolute right-0 top-1/2 transform -translate-y-1/2 z-30 w-8 h-12 flex items-center justify-center bg-white/90 backdrop-blur-sm border border-gray-400 rounded-l-lg hover:bg-white hover:border-blue-500 hover:shadow-lg transition-all ${!showRightScroll ? 'opacity-50 cursor-not-allowed' : ''}`}
              disabled={!showRightScroll}
            >
              <ChevronRight className="w-5 h-5 text-gray-600" />
            </button>

            <div className="container mx-auto px-10">
              <div className="flex items-center">
                {/* Schools Label */}
                

                {/* Scrollable School Links Container */}
                <div className="flex-1 relative overflow-hidden">
                  <div 
                    ref={schoolsContainerRef}
                    className="flex items-center flex-nowrap overflow-x-auto scrollbar-hide gap-1 py-3"
                    style={{ 
                      scrollbarWidth: 'none',
                      msOverflowStyle: 'none',
                      WebkitOverflowScrolling: 'touch'
                    }}
                    onScroll={checkScrollPosition}
                  >
                    {loadingSchools ? (
                      <div className="flex items-center px-6 py-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-3"></div>
                        <span className="text-sm text-gray-600">Loading schools...</span>
                      </div>
                    ) : schools.length === 0 ? (
                      <div className="px-6 py-2 text-sm text-gray-500">No schools available</div>
                    ) : (
                      schools.map((school) => (
                        <div 
                          key={school._id}
                          className="relative flex-shrink-0"
                        >
                          <div className="relative">
                            <button
                              className={`
                                flex items-center gap-2 px-4 py-2 mx-1 text-sm font-medium whitespace-nowrap transition-all rounded-lg border
                                ${activeSchoolId === school._id 
                                  ? 'text-blue-700 bg-white border-blue-400 shadow-md scale-105 z-[10001]' 
                                  : 'text-gray-700 border-transparent hover:text-blue-600 hover:bg-white hover:border-blue-300 hover:shadow z-[10000]'
                                }
                              `}
                              onMouseEnter={(e) => handleSchoolHover(school._id, e)}
                              onMouseLeave={handleSchoolMouseLeave}
                              onClick={(e) => handleSchoolClick(school._id, e)}
                            >
                              <Building2 className={`w-4 h-4 ${activeSchoolId === school._id ? 'text-blue-600' : 'text-gray-500 group-hover:text-blue-500'}`} />
                              {school.name}
                              <ChevronDown className={`w-3 h-3 transition-transform ${activeSchoolId === school._id ? 'rotate-180 text-blue-600' : 'text-gray-400 group-hover:text-blue-500'}`} />
                            </button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Departments Dropdown - FIXED: Stays under school link even when page scrolls */}
      {activeSchoolId && currentSchool && (
        <div 
          ref={dropdownRef}
          className="fixed animate-fadeIn"
          style={{
            left: `${dropdownPosition.left}px`,
            top: `${dropdownPosition.top}px`,
            position: 'fixed',
            zIndex: 2147483647, // Maximum 32-bit integer
          }}
          onMouseEnter={handleDropdownMouseEnter}
          onMouseLeave={handleDropdownMouseLeave}
        >
          <div className="bg-white border border-gray-300 rounded-lg shadow-2xl min-w-[320px] mt-1">
            {/* School Header */}
            <div className="p-4 bg-gradient-to-r from-blue-50 to-gray-50 border-b border-gray-300">
              <div className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-blue-600" />
                <div>
                  <h4 className="font-bold text-gray-800">{currentSchool?.name}</h4>
                  <p className="text-xs text-gray-600">Select department to view years</p>
                </div>
              </div>
            </div>
            
            {/* Departments List */}
            <div className="max-h-80 overflow-y-auto">
              {loadingDepts ? (
                <div className="flex justify-center p-6">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                </div>
              ) : departments.length === 0 ? (
                <div className="text-center p-6 text-gray-500">
                  <FolderOpen className="w-12 h-12 mx-auto text-gray-300 mb-2" />
                  No departments available
                </div>
              ) : (
                <div className="p-3">
                  {departments.map((dept) => (
                    <div key={dept._id} className="mb-2 last:mb-0">
                      <button
                        onClick={() => handleDeptClick(dept._id)}
                        className={`
                          w-full flex items-center justify-between p-3 text-left rounded-lg transition-all duration-200
                          ${selectedDeptId === dept._id 
                            ? 'bg-blue-50 border border-blue-200 shadow-sm' 
                            : 'hover:bg-gray-50 hover:shadow'
                          }
                        `}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg ${selectedDeptId === dept._id ? 'bg-blue-100' : 'bg-gray-100'}`}>
                            <Book className="w-4 h-4 text-blue-600" />
                          </div>
                          <div>
                            <div className="font-medium text-gray-800">{dept.name}</div>
                            <div className="text-xs text-gray-500">{dept.code || 'Department'}</div>
                          </div>
                        </div>
                        <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${selectedDeptId === dept._id ? 'rotate-180 text-blue-600' : 'text-gray-400'}`} />
                      </button>
                      
                      {/* Years for Selected Department */}
                      {selectedDeptId === dept._id && (
                        <div className="mt-2 ml-12 pl-4 border-l-2 border-blue-200 bg-white rounded-lg p-3 shadow-lg border border-gray-200">
                          <div className="flex items-center gap-2 mb-3">
                            <Calendar className="w-4 h-4 text-blue-500" />
                            <span className="text-sm font-semibold text-gray-700">Academic Years</span>
                          </div>
                          
                          {(() => {
                            const cacheKey = `${activeSchoolId}-${dept._id}`;
                            const years = deptYears[cacheKey] || [];
                            const isLoading = loadingYears[cacheKey];
                            
                            return isLoading ? (
                              <div className="flex justify-center p-3">
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                              </div>
                            ) : years.length > 0 ? (
                              <div className="grid grid-cols-3 gap-2">
                                {years.map((year) => (
                                  <button
                                    key={year}
                                    onClick={() => handleYearSelect(activeSchoolId, dept._id, year)}
                                    className="p-2 bg-white border border-gray-300 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-all hover:scale-105 text-center"
                                  >
                                    <div className="text-lg font-bold text-blue-600">{year}</div>
                                    <div className="text-xs text-gray-600">{getYearLabel(year)}</div>
                                  </button>
                                ))}
                              </div>
                            ) : (
                              <div className="text-sm text-gray-500 p-2 text-center">
                                No academic years available
                              </div>
                            );
                          })()}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Footer */}
            <div className="p-3 border-t border-gray-200 bg-gray-50">
              <Link 
                to={`/school/${activeSchoolId}`}
                onClick={closeDropdown}
                className="flex items-center justify-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-100 py-2 rounded-lg transition-colors"
              >
                <Eye className="w-4 h-4" />
                View all school materials
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Menu - SIDEBAR DRAWER STYLE */}
      {isMenuOpen && (
        <div className="fixed inset-0 z-[10000] flex lg:hidden animate-fade-in">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" 
            onClick={() => setIsMenuOpen(false)} 
          />
          
          {/* Drawer Content */}
          <div className="relative bg-white w-[80%] max-w-[320px] h-full shadow-2xl flex flex-col animate-slide-right mt-16 md:mt-20 lg:mt-0">
            {/* Mobile Header */}
            <div className="p-6 bg-gradient-to-br from-blue-900 to-indigo-900 text-white flex items-center justify-between">
              <div className="flex items-center gap-3">
                 <img src={dduLogo} className="w-8 h-8 rounded-full bg-white p-1" />
                 <span className="font-black uppercase tracking-widest text-[11px]">DDU Portal</span>
              </div>
              <button 
                onClick={() => setIsMenuOpen(false)}
                className="p-3 bg-white/10 rounded-xl hover:bg-white/20 transition-all"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto custom-scrollbar">
              {/* SECTION: NAVIGATION */}
              <div className="border-b border-slate-100">
                <button 
                  onClick={() => setExpandedMobileSection(expandedMobileSection === 'nav' ? null : 'nav')}
                  className={`w-full p-6 flex items-center justify-between transition-all ${expandedMobileSection === 'nav' ? 'bg-blue-50 text-blue-600' : 'text-slate-600'}`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`p-2 rounded-xl ${expandedMobileSection === 'nav' ? 'bg-blue-600 text-white' : 'bg-slate-100'}`}>
                      <LayoutDashboard className="w-5 h-5" />
                    </div>
                    <span className="font-black text-[11px] uppercase tracking-[0.2em]">Navigation</span>
                  </div>
                  <ChevronDown className={`w-4 h-4 transition-transform ${expandedMobileSection === 'nav' ? 'rotate-180' : ''}`} />
                </button>
                {expandedMobileSection === 'nav' && (
                  <div className="px-6 pb-6 space-y-2 animate-slide-down">
                    {mainNavItems.map((item) => (
                      <Link
                        key={item.path}
                        to={item.path}
                        className="flex items-center gap-4 p-4 rounded-xl hover:bg-slate-50 transition-all text-slate-500 hover:text-blue-600 group"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        <item.icon className="w-5 h-5 opacity-40 group-hover:opacity-100" />
                        <span className="font-black text-[10px] uppercase tracking-widest">{item.label}</span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              {/* SECTION: TEACHER/ADMIN PANEL */}
              <div className="border-b border-slate-100">
                <button 
                  onClick={() => setExpandedMobileSection(expandedMobileSection === 'panel' ? null : 'panel')}
                  className={`w-full p-6 flex items-center justify-between transition-all ${expandedMobileSection === 'panel' ? 'bg-purple-50 text-purple-600' : 'text-slate-600'}`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`p-2 rounded-xl ${expandedMobileSection === 'panel' ? 'bg-purple-600 text-white' : 'bg-slate-100'}`}>
                      <Shield className="w-5 h-5" />
                    </div>
                    <span className="font-black text-[11px] uppercase tracking-[0.2em]">{isAdmin ? 'Admin Panel' : 'Instructor Panel'}</span>
                  </div>
                  <ChevronDown className={`w-4 h-4 transition-transform ${expandedMobileSection === 'panel' ? 'rotate-180' : ''}`} />
                </button>
                {expandedMobileSection === 'panel' && (
                  <div className="px-6 pb-6 space-y-2 animate-slide-down">
                    {!user ? (
                      <div className="p-8 text-center bg-slate-50 rounded-2xl border border-dashed border-slate-200">
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-4">Please log in to access</p>
                        <button onClick={openLoginModal} className="px-6 py-3 bg-blue-600 text-white rounded-xl font-black text-[10px] uppercase tracking-widest shadow-lg shadow-blue-600/20">Login Now</button>
                      </div>
                    ) : dashboardItems.length > 0 ? (
                      dashboardItems.map((item) => (
                        <Link
                          key={item.path}
                          to={item.path}
                          className="flex items-center justify-between p-4 rounded-xl hover:bg-slate-50 transition-all text-slate-500 hover:text-purple-600 group"
                          onClick={() => setIsMenuOpen(false)}
                        >
                          <div className="flex items-center gap-4">
                            <item.icon className="w-5 h-5 opacity-40 group-hover:opacity-100" />
                            <span className="font-black text-[10px] uppercase tracking-widest">{item.label}</span>
                          </div>
                          {item.count > 0 && <span className="bg-red-500 text-white text-[9px] font-black px-2 py-0.5 rounded-full">{item.count}</span>}
                        </Link>
                      ))
                    ) : (
                      <p className="text-center p-8 text-slate-400 font-bold uppercase text-[10px] tracking-widest">No panel items available</p>
                    )}
                  </div>
                )}
              </div>

              {/* SECTION: SCHOOLS */}
              <div className="border-b border-slate-100">
                <button 
                  onClick={() => setExpandedMobileSection(expandedMobileSection === 'schools' ? null : 'schools')}
                  className={`w-full p-6 flex items-center justify-between transition-all ${expandedMobileSection === 'schools' ? 'bg-emerald-50 text-emerald-600' : 'text-slate-600'}`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`p-2 rounded-xl ${expandedMobileSection === 'schools' ? 'bg-emerald-600 text-white' : 'bg-slate-100'}`}>
                      <Building2 className="w-5 h-5" />
                    </div>
                    <span className="font-black text-[11px] uppercase tracking-[0.2em]">DDU Schools</span>
                  </div>
                  <ChevronDown className={`w-4 h-4 transition-transform ${expandedMobileSection === 'schools' ? 'rotate-180' : ''}`} />
                </button>
                {expandedMobileSection === 'schools' && (
                  <div className="px-6 pb-6 space-y-2 animate-slide-down">
                    {loadingSchools ? (
                      <div className="p-8 flex items-center justify-center gap-3">
                         <div className="w-4 h-4 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin"></div>
                         <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Loading...</span>
                      </div>
                    ) : schools.map((school) => (
                      <div key={school._id} className="space-y-1">
                        <button
                          className={`flex items-center justify-between w-full p-4 rounded-xl transition-all group text-left ${expandedMobileSchool === school._id ? 'bg-emerald-50 text-emerald-700 font-bold' : 'text-slate-500 hover:bg-slate-50'}`}
                          onClick={() => {
                            if (expandedMobileSchool === school._id) setExpandedMobileSchool(null);
                            else {
                              setExpandedMobileSchool(school._id);
                              setExpandedMobileDept(null);
                              if (!schoolDepts[school._id]) {
                                setLoadingDepts(true);
                                publicAPI.getSchoolDepartments(school._id)
                                  .then(res => setSchoolDepts(prev => ({ ...prev, [school._id]: res.data || [] })))
                                  .finally(() => setLoadingDepts(false));
                              }
                            }
                          }}
                        >
                          <div className="flex items-center gap-4">
                             <School className={`w-5 h-5 ${expandedMobileSchool === school._id ? 'text-emerald-600' : 'opacity-40 group-hover:opacity-100'}`} />
                             <span className="font-black text-[10px] uppercase tracking-widest leading-tight">{school.name}</span>
                          </div>
                          <ChevronDown className={`w-4 h-4 transition-transform ${expandedMobileSchool === school._id ? 'rotate-180' : ''}`} />
                        </button>
                        
                        {expandedMobileSchool === school._id && (
                          <div className="pl-8 space-y-1 animate-slide-down pb-2">
                             {loadingDepts && !schoolDepts[school._id] ? (
                               <div className="p-4 flex items-center gap-2"><div className="w-3 h-3 border border-emerald-500 border-t-transparent rounded-full animate-spin"></div></div>
                             ) : schoolDepts[school._id]?.map(dept => (
                               <div key={dept._id} className="space-y-1">
                                  <button 
                                    onClick={() => {
                                      if (expandedMobileDept === dept._id) setExpandedMobileDept(null);
                                      else {
                                        setExpandedMobileDept(dept._id);
                                        loadDepartmentYears(dept._id, school._id);
                                      }
                                    }}
                                    className={`w-full flex items-center justify-between p-3 rounded-lg text-[10px] font-bold uppercase tracking-widest ${expandedMobileDept === dept._id ? 'text-emerald-600 bg-emerald-50/50' : 'text-slate-500 hover:bg-slate-50'}`}
                                  >
                                    <span className="flex-1 pr-2">{dept.name}</span>
                                    <ChevronRight className={`w-3 h-3 transition-transform ${expandedMobileDept === dept._id ? 'rotate-90' : ''}`} />
                                  </button>
                                  
                                  {expandedMobileDept === dept._id && (
                                    <div className="pl-4 py-2 flex flex-wrap gap-2 animate-slide-down">
                                       {deptYears[`${school._id}-${dept._id}`]?.map(year => (
                                         <button
                                           key={year}
                                           onClick={() => handleYearSelect(school._id, dept._id, year)}
                                           className="px-3 py-2 bg-white border border-slate-200 rounded-lg text-emerald-600 font-black text-[10px] shadow-sm hover:border-emerald-500"
                                         >
                                           YR {year}
                                         </button>
                                       ))}
                                    </div>
                                  )}
                               </div>
                             ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* User Profile / Auth Action */}
            <div className="p-8 border-t border-slate-100 bg-slate-50/50">
               {user ? (
                 <div className="space-y-2">
                   <button onClick={handleLogout} className="flex items-center justify-center gap-4 w-full p-5 bg-red-50 text-red-600 rounded-2xl font-black text-[11px] uppercase tracking-[0.2em] shadow-sm hover:bg-red-100 transition-all">
                      <LogOut className="w-5 h-5" /> Sign Out
                   </button>
                 </div>
               ) : (
                 <button onClick={openLoginModal} className="flex items-center justify-center gap-4 w-full p-5 bg-blue-600 text-white rounded-2xl font-black text-[11px] uppercase tracking-[0.2em] shadow-xl shadow-blue-600/20 transition-all">
                    <User className="w-5 h-5" /> Access Portal
                 </button>
               )}
            </div>
          </div>
        </div>
      )}

      {/* Login Modal */}
      {modalConfig.isOpen && (
        <LoginModal
          onClose={closeModal}
          onLoginSuccess={handleLoginSuccess}
          defaultMode={modalConfig.defaultMode}
        />
      )}

      {/* Add CSS animation */}
      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideRight { from { transform: translateX(-100%); } to { transform: translateX(0); } }
        @keyframes slideDown { from { transform: translateY(-10px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        
        .animate-fadeIn { animation: fadeIn 0.15s ease-out; }
        .animate-slide-right { animation: slideRight 0.3s cubic-bezier(0.16, 1, 0.3, 1); }
        .animate-slide-down { animation: slideDown 0.2s ease-out; }
        
        /* Custom scrollbar for schools container */
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </>
  );
};

export default Header;
